import axios from 'axios';
import * as rax from 'retry-axios';
import { TokenExpiredError } from '@honeybook/core';
import { HB_BASE_ERROR_CODES } from '@honeybook/utils';
import { getApiBaseUrl } from '@honeybook/utils-web';
import { DEFAULT_HEADERS, DEFAULT_RETRY_CONFIG, REQUEST_TIMEOUT } from './http-client.constants';
import { isUnauthorizedError } from './request-error.utils';
/**
 * Shared Axios transport for HoneyBook web apps — base URL, api-version header,
 * retry, timeout, request/response middlewares, and HB error handling.
 *
 * Domain- and session-agnostic: it deliberately leaves the one session-dependent
 * decision — what to do on an HB unauthorized error — `abstract`. Concrete
 * clients slot in per session model:
 *   • `UnauthenticatedHttpClient` (signup, CX shell) → clear local auth + reload
 *   • an authenticated client → session recovery, plus `getAuthHeaders`
 * Overridable `protected` defaults: `getAuthHeaders` (none),
 * `shouldUseCredentials` (no cookies), `getBaseUrl`, `onWrongApiVersion`.
 */
export class BaseHttpClient {
    /**
     * `transport` defaults to a fresh per-instance Axios — never the global
     * `axios` — so constructing/using a client can't patch or double-attach
     * interceptors on the shared axios (which src's `BaseApiService` also
     * attaches to). It is injectable so tests can drive the real transport
     * (interceptors, retry, error handling) through a stubbed `adapter` without
     * mocking the `axios`/`retry-axios` modules.
     */
    constructor(logger, transport = axios.create()) {
        this.logger = logger;
        this.config = {};
        this.requestMiddlewares = [];
        this.responseMiddlewares = [];
        this.unauthorizedHandlers = new Set();
        this.axiosInstance = transport;
        // retry-axios defaults `raxConfig.instance` to the instance it is
        // attached to, so retries already reissue through this same instance —
        // no need to point it back here explicitly.
        this.axiosInstance.defaults.raxConfig = { ...DEFAULT_RETRY_CONFIG };
        // Must stay before `setupInterceptors()`: response interceptors run in
        // registration order, so retry-axios gets its chance first and
        // middlewares only see errors it has given up on.
        rax.attach(this.axiosInstance);
        this.setupInterceptors();
    }
    setupInterceptors() {
        this.axiosInstance.interceptors.request.use(async (initialConfig) => {
            let updatedConfig = initialConfig;
            for (const middleware of this.requestMiddlewares) {
                updatedConfig = await middleware(updatedConfig);
            }
            return updatedConfig;
        });
        // Registered as two separate interceptors, never one paired call: a
        // promise does not route a throw from `onFulfilled` to its own paired
        // `onRejected`, so pairing them would hide such a throw from the error
        // middlewares below. Order matters — fulfilled must come first.
        this.axiosInstance.interceptors.response.use(async (initialResponse) => {
            let updatedResponse = initialResponse;
            for (const { onFulfilled } of this.responseMiddlewares) {
                if (onFulfilled) {
                    updatedResponse = await onFulfilled(updatedResponse);
                }
            }
            return updatedResponse;
        });
        this.axiosInstance.interceptors.response.use(undefined, 
        // `onRejected`'s return value is ignored — the original error always
        // propagates, so a middleware cannot recover the failed request.
        async (initialError) => {
            for (const { onRejected } of this.responseMiddlewares) {
                try {
                    await onRejected?.(initialError);
                }
                catch (middlewareError) {
                    this.logger.error(middlewareError);
                }
            }
            return Promise.reject(initialError);
        });
    }
    addRequestMiddleware(middleware) {
        this.requestMiddlewares.push(middleware);
        return () => {
            this.requestMiddlewares = this.requestMiddlewares.filter(m => m !== middleware);
        };
    }
    addResponseMiddleware(onFulfilled, onRejected) {
        const middleware = { onFulfilled, onRejected };
        this.responseMiddlewares.push(middleware);
        return () => {
            this.responseMiddlewares = this.responseMiddlewares.filter(m => m !== middleware);
        };
    }
    /**
     * Every registered handler runs on each HB unauthorized error. If any
     * returns `true` it claims the error and `onUnauthorizedError` is skipped
     * for that request; the request still rejects with `TokenExpiredError`.
     * A claim means the app is recovering the session itself, e.g. by
     * re-verifying the visitor, so the client must not clear it. A handler
     * that throws is logged and counts as not claiming.
     */
    addUnauthorizedHandler(handler) {
        this.unauthorizedHandlers.add(handler);
        return () => this.unauthorizedHandlers.delete(handler);
    }
    /**
     * Supply config once it exists (after the app's config fetch resolves).
     * Before this runs, requests go out without the api-version header —
     * matching the first, pre-config fetch.
     */
    init({ config }) {
        this.config = config;
    }
    async request({ method = 'get', url, params = {}, data = {}, headers }) {
        try {
            const manipulatedConfig = this.requestInterceptor({
                method,
                url,
                params,
                data,
                headers: { ...DEFAULT_HEADERS, ...headers }
            });
            const response = await this.axiosInstance({
                baseURL: this.getBaseUrl(),
                timeout: REQUEST_TIMEOUT,
                withCredentials: this.shouldUseCredentials(),
                ...manipulatedConfig
            });
            return response.data;
        }
        catch (err) {
            return await this.handleRoutineError(err);
        }
    }
    handleRoutineError(err) {
        if (!err?.response?.data) {
            // Not an API error — probably a network error, where err.response
            // is an XMLHttpRequest with an empty body and the message is on err.
            return Promise.reject(err);
        }
        const { error_type } = err.response.data;
        if (!error_type) {
            return Promise.reject(err.response);
        }
        const { hb_base_error_codes } = this.config;
        if (!hb_base_error_codes) {
            this.logger.error(new Error('hb_base_error_codes is not defined', { cause: err }));
        }
        const errorCodes = hb_base_error_codes || HB_BASE_ERROR_CODES;
        if (isUnauthorizedError(err)) {
            this.logger.error(new Error('Token expired', {
                cause: new Error(HB_BASE_ERROR_CODES.unauthorized_error)
            }));
            if (!this.runUnauthorizedHandlers(err)) {
                this.onUnauthorizedError(err);
            }
            return Promise.reject(new TokenExpiredError());
        }
        if (error_type === errorCodes.wrong_api_version_error) {
            // After the server deploys, the api_version is changed.
            this.logger.error(new Error('Wrong server api version, reloading...', {
                cause: new Error(errorCodes.wrong_api_version_error)
            }));
            this.onWrongApiVersion();
        }
        return Promise.reject(err.response);
    }
    runUnauthorizedHandlers(error) {
        let claimed = false;
        for (const handler of this.unauthorizedHandlers) {
            try {
                if (handler(error) === true) {
                    claimed = true;
                }
            }
            catch (handlerError) {
                this.logger.error(handlerError);
            }
        }
        return claimed;
    }
    requestInterceptor(config) {
        const headers = this.getHeaders();
        return { ...config, headers: { ...headers, ...config.headers } };
    }
    // ── Template methods: shared defaults, overridden per domain ──────────────
    /** API base URL. Defaults to the environment-derived HB API host. */
    getBaseUrl() {
        return getApiBaseUrl();
    }
    /** Whether to send credentials (cookies) with the request. Default: no. */
    shouldUseCredentials() {
        return false;
    }
    /**
     * Runs on an api-version mismatch (server deployed a newer contract).
     * Default: hard reload to pick up the new client.
     */
    onWrongApiVersion() {
        window.location.reload();
    }
    /** Session headers for each request. Default: none. */
    getAuthHeaders() {
        return {};
    }
    /** The api-version header, once config is available, plus any auth headers. */
    getHeaders() {
        const { hb_api_headers, api_version } = this.config;
        const apiVersionHeaders = hb_api_headers && api_version
            ? {
                [hb_api_headers.api_client_version || '']: api_version + ''
            }
            : {};
        return { ...apiVersionHeaders, ...this.getAuthHeaders() };
    }
}
//# sourceMappingURL=base-http-client.js.map